import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";

import type { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";
import { styled } from "simplydo/react/styled";
import {
  Button,
  Form,
  Grid,
  Header,
  Icon,
  Label,
  Radio,
} from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import actions from "actions";
import api from "api";
import { useAppSelector } from "store";
import { useUserReload } from "utils/useUserReload";

import AdjustableUserList from "components/lib/AdjustableUserList";

import type { GroupDetail } from "./types";

const sections = ["Name", "Type", "Members"];

const FormatField = styled(Form.Field)`
  display: flex;
  border-width: 1px;
  border-color: #c3cfd9;
  border-style: solid;
  padding: 30px;
  margin: 10px 0px 10px 0px;
  align-items: center;
`;

const FormatInfo = styled.div`
  text-align: left;
  margin-left: 20px;
`;

type GroupState = {
  name: string;
  visibility: string;
  currentSection: string;
  completedSections: string[];
  addedMembers: OpenAPI.Schemas["User"][];
};

type MemberSearchState = { memberSearchResults: OpenAPI.Schemas["User"][] };

const NewGroup = () => {
  const user = useAppSelector((state) => state.user);
  const [newGroupState, setNewGroupState] = useState<GroupState>({
    name: "",
    visibility: "open",
    currentSection: "Name",
    completedSections: [],
    addedMembers: user ? [user] : [],
  });
  const [searchState, setSearchState] = useState<MemberSearchState>({
    memberSearchResults: [],
  });
  const [group, setGroup] = useState<GroupDetail | null>(null);
  const groupId = group?._id;
  const dispatch = useDispatch();
  const reloadUser = useUserReload();
  const [loadingUsers, setLoadingUsers] = useState(false);

  const { t } = useTranslation();

  const { name, visibility, currentSection, completedSections, addedMembers } =
    newGroupState;
  const { memberSearchResults } = searchState;

  const nextPage = useCallback(() => {
    const currentSectionIndex = sections.indexOf(currentSection);
    const nextSection = sections[currentSectionIndex + 1];
    if (!nextSection) return;
    setNewGroupState((prevState) => ({
      ...prevState,
      currentSection: nextSection,
      completedSections: [...prevState.completedSections, currentSection],
    }));
  }, [currentSection]);

  const saveGroup = useCallback(
    (type: string, value: string | boolean) => {
      if (!groupId) return;
      api.groups.update(
        groupId,
        { [type]: value },
        (data) => setGroup(data as GroupDetail),
        (err) => toast.error(err.message),
      );
    },
    [groupId],
  );

  const createGroup = useCallback(() => {
    api.groups.create(
      name,
      (data) => {
        setGroup(data as GroupDetail);
        dispatch(actions.user.createGroup(data._id, name));
        reloadUser();
        nextPage();
      },
      (err) => toast.error(err.message),
    );
  }, [name, nextPage, dispatch, reloadUser]);

  const keyFunction = useCallback(
    (event: KeyboardEvent) => {
      if (event.keyCode === 13 && currentSection === "Name" && name) {
        createGroup();
      }
    },
    [currentSection, name, createGroup],
  );

  useEffect(() => {
    document.addEventListener("keydown", keyFunction, false);
    return () => {
      document.removeEventListener("keydown", keyFunction, false);
    };
  }, [keyFunction]);

  const throttledSearch = useThrottle(
    (searchGroupId: string, memberSearch: string) => {
      api.search.groupMembers(
        searchGroupId,
        { query: memberSearch },
        ({ users }) => {
          setLoadingUsers(false);
          setSearchState((prevState) => ({
            ...prevState,
            memberSearchResults: users,
          }));
        },
        (err) => {
          setLoadingUsers(false);
          toast.error(err.message);
        },
      );
    },
    500,
  );

  const searchMembers = useCallback(
    (memberSearch: string) => {
      if (!memberSearch) {
        setLoadingUsers(false);
        setSearchState((prevState) => ({
          ...prevState,
          memberSearchResults: [],
        }));
        return;
      }
      if (!groupId) return;

      setLoadingUsers(true);
      throttledSearch(groupId, memberSearch);
    },
    [groupId, throttledSearch],
  );

  const addMember = useCallback(
    (newUser: OpenAPI.Schemas["User"]) => {
      if (!groupId) return;
      api.groups.join(
        groupId,
        newUser._id,
        () => {
          setNewGroupState((prevState) => ({
            ...prevState,
            addedMembers: [...prevState.addedMembers, newUser],
          }));
        },
        (err) => toast.error(err.message),
      );
    },
    [groupId],
  );

  const removeMember = useCallback(
    (removedUser: OpenAPI.Schemas["User"]) => {
      if (!groupId) return;
      api.groups.leave(
        groupId,
        removedUser._id,
        () => {
          setNewGroupState((prevState) => ({
            ...prevState,
            addedMembers: prevState.addedMembers.filter(
              (m) => m._id !== removedUser._id,
            ),
          }));
        },
        (err) => toast.error(err.message),
      );
    },
    [groupId],
  );

  const updateVisibility = useCallback(() => {
    if (visibility === "open") return;
    if (visibility === "public") saveGroup("isPublic", true);
    if (visibility === "private") saveGroup("isClosed", true);
  }, [visibility, saveGroup]);

  const memberActionButton = useCallback(
    (member: OpenAPI.Schemas["User"]) => {
      const isAddedToGroup = addedMembers.find((m) => m._id === member._id);
      return (
        <Button
          onClick={() => {
            if (isAddedToGroup) removeMember(member);
            else addMember(member);
          }}
          basic={!isAddedToGroup}
          primary
        >
          <Icon name="add user" />
          {isAddedToGroup
            ? t("groups.new.members.remove")
            : t("groups.new.members.add")}
        </Button>
      );
    },
    [t, addedMembers, removeMember, addMember],
  );

  const renderName = useMemo(
    () => (
      <div>
        <Header as="h2">{t("groups.new.name.title")}</Header>
        <p style={{ marginTop: 50, fontSize: "17px" }}>
          {t("groups.new.name.info")}
        </p>
        <Input
          placeholder={t("groups.new.name.placeholder")}
          size="big"
          style={{ marginTop: 50 }}
          value={name}
          onChange={(_e, { value }) =>
            setNewGroupState((prevState) => ({ ...prevState, name: value }))
          }
          fluid
        />
        <p style={{ fontSize: "13px", margin: "10px" }}>
          {t("groups.new.name.info2")}
        </p>
        <Button
          style={{ marginTop: 50 }}
          disabled={name.length < 5}
          size="large"
          secondary
          onClick={() => createGroup()}
        >
          {t("generic.next")}
          <Icon name="arrow right" />
        </Button>
      </div>
    ),
    [name, t, createGroup],
  );

  const renderType = useMemo(
    () => (
      <div style={{ textAlign: "center" }}>
        <Header as="h2">{t("groups.new.type.title", { name })}</Header>
        <Form>
          <FormatField>
            <Radio
              name="visibility"
              value="open"
              checked={visibility === "open"}
              onChange={(_e, { value }) =>
                setNewGroupState((prevState) => ({
                  ...prevState,
                  visibility: String(value),
                }))
              }
            />
            <FormatInfo>
              <Header size="medium">
                {t("groups.new.type.openOrg.title")}{" "}
                <Label>{t("generic.default")}</Label>
              </Header>
              <p>{t("groups.new.type.openOrg.info")}</p>
            </FormatInfo>
          </FormatField>
          <FormatField>
            <Radio
              name="visibility"
              value="private"
              checked={visibility === "private"}
              onChange={(_e, { value }) =>
                setNewGroupState((prevState) => ({
                  ...prevState,
                  visibility: String(value),
                }))
              }
            />
            <FormatInfo>
              <Header size="medium">
                {t("groups.new.type.private.title")}
              </Header>
              <p>{t("groups.new.type.private.info")}</p>
            </FormatInfo>
          </FormatField>
        </Form>
        <Button
          style={{ marginTop: 20 }}
          size="large"
          secondary
          onClick={() => {
            searchMembers("");
            nextPage();
            updateVisibility();
          }}
        >
          {t("generic.next")}
          <Icon name="arrow right" />
        </Button>
      </div>
    ),
    [t, visibility, name, searchMembers, nextPage, updateVisibility],
  );

  const renderMembers = useMemo(
    () => (
      <div>
        <Header as="h2">{t("groups.new.members.title")}</Header>
        <AdjustableUserList
          loading={loadingUsers}
          results={memberSearchResults}
          searchPlaceholder="Start typing a name..."
          searchFunction={searchMembers}
          actions={memberActionButton}
        />
        <p style={{ fontSize: "13px", margin: "10px" }}>
          {t("groups.new.members.info")}
        </p>
        <Button
          style={{ marginTop: 20 }}
          as={Link}
          to={`/groups/${groupId}`}
          secondary
        >
          {t("generic.finish")}
          <Icon name="arrow right" />
        </Button>
      </div>
    ),
    [
      t,
      memberSearchResults,
      groupId,
      searchMembers,
      memberActionButton,
      loadingUsers,
    ],
  );

  return (
    <Grid container centered stackable>
      <Grid.Row columns={3}>
        {sections.map((section) => {
          let style;
          if (section === currentSection)
            style = { backgroundColor: "#DFE7ED", color: "#DFE7ED" };
          else if (completedSections.includes(section))
            style = { backgroundColor: "#4B5C6B", color: "#fff" };
          else style = { backgroundColor: "#fff", color: "#fff" };
          if (section === "Done") return null;
          return (
            <Grid.Column key={section} width={2} textAlign="center">
              <Icon name="checkmark" bordered circular style={style} />{" "}
              {section}
            </Grid.Column>
          );
        })}
      </Grid.Row>
      <Grid.Column
        textAlign="center"
        width={8}
        verticalAlign="middle"
        style={{ marginTop: 40, minHeight: 300, marginBottom: 40 }}
      >
        {(() => {
          switch (currentSection) {
            case "Name":
              return renderName;
            case "Type":
              return renderType;
            case "Members":
              return renderMembers;
            default:
              return null;
          }
        })()}
      </Grid.Column>
    </Grid>
  );
};

export default NewGroup;
